import React, { Component } from 'react';
import styled from 'styled-components';

import MGLogo from '../../../containers/assets/LandingPageLogo.png';

// Shared chrome for the instructor first-login onboarding flow.
// Full-viewport takeover, no leftbar or dashboard header.
// Design tokens from Figma (Guided Tours file):
//   bg gradient  linear-gradient(146.31deg, #2A4169 51%, rgba(255,255,255,0.9) 99%)
//   card         white or #EBF2FF depending on page
//   fonts        Mukta Mahee + Mukta Vaani (Bold/Medium/Regular)
//   button       linear-gradient(122.687deg, #FF5252, rgba(112,161,255,0.9)) - red -> blue
//   primary/blue heading/label color #2F4166
//   navy border  #2A4169 on header
//   outline      #D5D9DD tag borders + header bottom border

const PageBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(146.31deg, #2A4169 51.4%, rgba(255, 255, 255, 0.9) 99%);
  overflow: auto;
`;

const Header = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 64px;
  background: #FFFFFF;
  border-bottom: 2px solid #D5D9DD;
  display: flex;
  align-items: center;
  padding: 0 32px;
  z-index: 2;
`;

const HeaderLogo = styled.img`
  height: 32px;
  width: auto;
`;

const Content = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  box-sizing: border-box;
`;

const ProgressWrap = styled.div`
  width: 100%;
  max-width: 772px;
  margin-bottom: 24px;
`;

const ProgressLabels = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: 'Mukta Mahee', 'Mukta Vaani', 'Inter', sans-serif;
  font-weight: 400;
  font-size: 20px;
  color: #FFFFFF;
  margin-bottom: 12px;
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 10px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.2);
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: 10px;
  background: #FFFFFF;
  width: ${p => p.percent}%;
  transition: width 240ms ease-out;
`;

const Card = styled.div`
  width: 100%;
  max-width: ${p => p.maxWidth || 772}px;
  background: ${p => p.background || '#EBF2FF'};
  border-radius: 10px;
  padding: ${p => p.padding || '32px 67px 40px'};
  box-sizing: border-box;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
`;

const PrimaryButton = styled.button`
  display: block;
  width: 100%;
  height: 40px;
  border: none;
  border-radius: 10px;
  color: #FFFFFF;
  font-family: 'Mukta Mahee', 'Mukta Vaani', 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  background: linear-gradient(122.687deg, #FF5252 2.88%, rgba(112, 161, 255, 0.9) 100%);
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
  transition: opacity 200ms ease;

  &:hover { opacity: 0.94; }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const BackLink = styled.button`
  display: block;
  width: 100%;
  margin-top: 24px;
  background: transparent;
  border: none;
  padding: 0;
  color: rgba(0, 27, 59, 0.38);
  font-family: 'Mukta Mahee', 'Mukta Vaani', 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  cursor: pointer;
  text-decoration: underline;

  &:hover { color: rgba(0, 27, 59, 0.7); }
`;

class OnboardingLayout extends Component {
  render() {
    const {
      children,
      progressStep,
      progressTotal,
      buttonLabel,
      onButtonClick,
      buttonDisabled,
      onBack,
      cardMaxWidth,
      cardPadding,
      cardBackground,
    } = this.props;

    const showProgress = progressTotal > 0;
    const percent = showProgress
      ? Math.max(0, Math.min(100, (progressStep / progressTotal) * 100))
      : 0;

    return (
      <PageBackground>
        <Header>
          <HeaderLogo src={MGLogo} alt="Market Games" />
        </Header>
        <Content hasProgress={showProgress}>
          {showProgress && (
            <ProgressWrap>
              <ProgressLabels>
                <span>Progress</span>
                <span>{progressStep} of {progressTotal}</span>
              </ProgressLabels>
              <ProgressTrack>
                <ProgressFill percent={percent} />
              </ProgressTrack>
            </ProgressWrap>
          )}
          <Card
            maxWidth={cardMaxWidth}
            padding={cardPadding}
            background={cardBackground}
          >
            {children}
            {buttonLabel && (
              <PrimaryButton
                onClick={onButtonClick}
                disabled={!!buttonDisabled}
              >
                {buttonLabel}
              </PrimaryButton>
            )}
            {onBack && (
              <BackLink onClick={onBack}>Back</BackLink>
            )}
          </Card>
        </Content>
      </PageBackground>
    );
  }
}

export default OnboardingLayout;



// WEBPACK FOOTER //
// ./src/instructor/components/onboarding/OnboardingLayout.js